/**
 * HTML List manipulation utilities.
 * Used by WebsiteUpdater to apply list item changes to HTML content.
 */

import {
  getHtmlFromDoc,
  HtmlUpdateResult,
  parseHtmlAndFindElement,
} from './html-element-utils';

// ============================================================================
// Constants (must match apps/sites/src/utils/editable-parser-utils.ts)
// ============================================================================

const SF_LIST_ITEM_ATTR = 'data-sf-list-item';
const SF_CMS_ENTRY_ATTR = 'data-sf-cms-entry';
const SF_LIST_ITEM_HIDDEN_ATTR = 'data-sf-list-item-hidden';

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Find all list items within a list container
 */
function getListItems(listElement: Element): Element[] {
  const items: Element[] = [];

  const findItems = (element: Element) => {
    for (const child of Array.from(element.children)) {
      if (child.hasAttribute(SF_LIST_ITEM_ATTR)) {
        items.push(child);
      } else {
        // Continue searching in nested elements (but not inside other list items)
        findItems(child);
      }
    }
  };

  findItems(listElement);
  return items;
}

// ============================================================================
// Apply Functions
// ============================================================================

/**
 * Delete a list item by ID.
 */
export function applyDeleteHtmlListItem(
  html: string,
  listPath: string,
  itemId: string,
): HtmlUpdateResult {
  const result = parseHtmlAndFindElement(html, listPath);
  if (!result) {
    return { success: false, error: `List not found: ${listPath}` };
  }

  const { doc, element: listElement } = result;
  const items = getListItems(listElement);

  // Don't allow deleting the last item
  if (items.length <= 1) {
    return { success: false, error: 'Cannot delete the last item in a list' };
  }

  const itemToDelete = items.find(
    (item) => item.getAttribute(SF_LIST_ITEM_ATTR) === itemId,
  );

  if (!itemToDelete) {
    return { success: false, error: `Item not found: ${itemId}` };
  }

  itemToDelete.remove();

  return { success: true, html: getHtmlFromDoc(doc) };
}

/**
 * Duplicate a specific list item.
 * Idempotent: if newItemId already exists, the operation is a no-op.
 */
export function applyDuplicateHtmlListItem(
  html: string,
  listPath: string,
  itemId: string,
  newItemId: string,
): HtmlUpdateResult {
  const result = parseHtmlAndFindElement(html, listPath);
  if (!result) {
    return { success: false, error: `List not found: ${listPath}` };
  }

  const { doc, element: listElement } = result;
  const items = getListItems(listElement);

  // Idempotency check: if newItemId already exists, this update was already applied
  const existingNewItem = items.find(
    (item) => item.getAttribute(SF_LIST_ITEM_ATTR) === newItemId,
  );
  if (existingNewItem) {
    // Already applied - return current HTML unchanged
    return { success: true, html: getHtmlFromDoc(doc) };
  }

  const itemToDuplicate = items.find(
    (item) => item.getAttribute(SF_LIST_ITEM_ATTR) === itemId,
  );

  if (!itemToDuplicate) {
    return { success: false, error: `Item not found: ${itemId}` };
  }

  // Clone the item
  const newItem = itemToDuplicate.cloneNode(true) as Element;

  // Use the provided ID (for idempotency)
  newItem.setAttribute(SF_LIST_ITEM_ATTR, newItemId);

  // Remove CMS binding from the new item (it's a new, independent item)
  newItem.removeAttribute(SF_CMS_ENTRY_ATTR);

  // Insert after the original item
  itemToDuplicate.parentNode?.insertBefore(
    newItem,
    itemToDuplicate.nextSibling,
  );

  return { success: true, html: getHtmlFromDoc(doc) };
}

/**
 * Move a list item up or down.
 */
export function applyMoveHtmlListItem(
  html: string,
  listPath: string,
  itemId: string,
  direction: 'up' | 'down',
): HtmlUpdateResult {
  const result = parseHtmlAndFindElement(html, listPath);
  if (!result) {
    return { success: false, error: `List not found: ${listPath}` };
  }

  const { doc, element: listElement } = result;
  const items = getListItems(listElement);

  const itemIndex = items.findIndex(
    (item) => item.getAttribute(SF_LIST_ITEM_ATTR) === itemId,
  );

  if (itemIndex === -1) {
    return { success: false, error: `Item not found: ${itemId}` };
  }

  const itemToMove = items[itemIndex];

  if (direction === 'up') {
    if (itemIndex === 0) {
      return { success: false, error: 'Item is already at the top' };
    }

    // Find the previous item and insert before it
    const prevItem = items[itemIndex - 1];
    prevItem.parentNode?.insertBefore(itemToMove, prevItem);
  } else {
    if (itemIndex === items.length - 1) {
      return { success: false, error: 'Item is already at the bottom' };
    }

    // Find the next item and insert after it
    const nextItem = items[itemIndex + 1];
    nextItem.parentNode?.insertBefore(itemToMove, nextItem.nextSibling);
  }

  return { success: true, html: getHtmlFromDoc(doc) };
}

/**
 * Toggle hidden state for a specific list item.
 * Idempotent: setting the same visibility state repeatedly is a no-op.
 */
export function applyToggleHtmlListItemHidden(
  html: string,
  listPath: string,
  itemId: string,
  isHidden: boolean,
): HtmlUpdateResult {
  const result = parseHtmlAndFindElement(html, listPath);
  if (!result) {
    return { success: false, error: `List not found: ${listPath}` };
  }

  const { doc, element: listElement } = result;
  const items = getListItems(listElement);

  const targetItem = items.find(
    (item) => item.getAttribute(SF_LIST_ITEM_ATTR) === itemId,
  );

  if (!targetItem) {
    return { success: false, error: `Item not found: ${itemId}` };
  }

  if (isHidden) {
    targetItem.setAttribute(SF_LIST_ITEM_HIDDEN_ATTR, 'true');
  } else {
    targetItem.removeAttribute(SF_LIST_ITEM_HIDDEN_ATTR);
  }

  return { success: true, html: getHtmlFromDoc(doc) };
}
